import React, { useMemo } from "react";
import { useNavigate } from "react-router-dom";
import { ArrowRight, CheckCircle } from "lucide-react";
import { addDaysToDate, DEFAULT_DUE_DAYS } from "../../hooks/useAppState";
import { getClientColor, getClientInitials } from "../../utils/clientColors";
import InvoiceStatusBadge from "./InvoiceStatusBadge";

// Formattazione italiana: 1.234,56 €
function formatCurrency(value) {
  const num = Number(value || 0);
  const abs = Math.abs(num);
  const sign = num < 0 ? "-" : "";
  const [intPart, decPart] = abs.toFixed(2).split(".");
  const groupedInt = intPart.replace(/\B(?=(\d{3})+(?!\d))/g, ".");
  return `${sign}${groupedInt},${decPart} €`;
}

// Format date in Italian
function formatDate(dateStr) {
  if (!dateStr) return "";
  const d = new Date(dateStr);
  return d.toLocaleDateString("it-IT", {
    day: "2-digit",
    month: "2-digit",
    year: "numeric",
  });
}

// Calculate days overdue/until due
function getDaysOverdue(dueDate, today) {
  const d1 = new Date(dueDate);
  const d2 = new Date(today);
  const diffTime = d2 - d1;
  return Math.floor(diffTime / (1000 * 60 * 60 * 24));
}

function getDaysUntilDue(dueDate, today) {
  const d1 = new Date(today);
  const d2 = new Date(dueDate);
  const diffTime = d2 - d1;
  return Math.floor(diffTime / (1000 * 60 * 60 * 24));
}

export default function OverdueInvoicesCard({ invoices, selectedYear, darkMode }) {
  const navigate = useNavigate();
  const today = new Date().toISOString().split("T")[0];

  const { clientsData, totalAmount, totalClients, hasOverdue } = useMemo(() => {
    const getYear = (dateStr) => (dateStr ? new Date(dateStr).getFullYear() : 0);

    // Filter unpaid invoices for selected year
    const unpaidInvoices = invoices.filter((inv) => {
      if (inv.paid) return false;
      if (!inv.issueDate) return false;
      return getYear(inv.issueDate) === selectedYear;
    });

    // Group by client
    const clientMap = new Map();

    unpaidInvoices.forEach((inv) => {
      const dueDate = inv.dueDate || addDaysToDate(inv.issueDate, DEFAULT_DUE_DAYS);
      const isOverdue = dueDate < today;
      const clientName = inv.clientName || "Cliente sconosciuto";

      if (!clientMap.has(clientName)) {
        clientMap.set(clientName, {
          name: clientName,
          invoices: [],
          totalAmount: 0,
          hasOverdue: false,
          oldestDueDate: dueDate,
          daysOverdue: 0,
          daysUntilDue: 0,
        });
      }

      const client = clientMap.get(clientName);
      client.invoices.push(inv);
      client.totalAmount += Number(inv.grossAmount) || 0;

      if (isOverdue) {
        client.hasOverdue = true;
        const days = getDaysOverdue(dueDate, today);
        if (days > client.daysOverdue) {
          client.daysOverdue = days;
          client.oldestDueDate = dueDate;
        }
      } else {
        const days = getDaysUntilDue(dueDate, today);
        if (!client.hasOverdue && dueDate < client.oldestDueDate) {
          client.oldestDueDate = dueDate;
          client.daysUntilDue = days;
        }
      }
    });

    // Convert to array and sort (overdue first, then by amount)
    const clients = Array.from(clientMap.values()).sort((a, b) => {
      if (a.hasOverdue && !b.hasOverdue) return -1;
      if (!a.hasOverdue && b.hasOverdue) return 1;
      return b.totalAmount - a.totalAmount;
    });

    const total = clients.reduce((sum, c) => sum + c.totalAmount, 0);
    const overdueClients = clients.filter((c) => c.hasOverdue);
    const pendingClients = clients.filter((c) => !c.hasOverdue);

    return {
      clientsData: clients,
      totalAmount: total,
      totalClients: clients.length,
      hasOverdue: overdueClients.length > 0,
      hasPending: pendingClients.length > 0,
    };
  }, [invoices, selectedYear, today]);

  // All invoices paid
  if (totalClients === 0) {
    return (
      <div
        className={`
          rounded-xl border p-6
          transition-all duration-200
          ${darkMode
            ? "bg-neutral-900 border-neutral-800 shadow-md"
            : "bg-white border-neutral-200 shadow-sm"
          }
        `}
      >
        <div className="flex items-center justify-between mb-4">
          <h3 className={`text-sm font-medium ${darkMode ? "text-neutral-500" : "text-neutral-600"}`}>
            Fatture da incassare
          </h3>
          <button
            onClick={() => navigate("/invoices")}
            className={`flex items-center gap-1 text-sm font-medium transition-colors ${
              darkMode
                ? "text-neutral-500 hover:text-neutral-400"
                : "text-neutral-600 hover:text-neutral-700"
            }`}
          >
            Vedi tutte
            <ArrowRight className="w-4 h-4" />
          </button>
        </div>

        <div className="flex items-center gap-3 py-4">
          <div className={`p-2 rounded-full ${darkMode ? "bg-emerald-900/30" : "bg-emerald-50"}`}>
            <CheckCircle className={`w-5 h-5 ${darkMode ? "text-emerald-400" : "text-emerald-600"}`} />
          </div>
          <div>
            <p className={`font-medium ${darkMode ? "text-white" : "text-neutral-900"}`}>
              Tutto in regola
            </p>
            <p className={`text-sm ${darkMode ? "text-neutral-500" : "text-neutral-600"}`}>
              Tutte le fatture {selectedYear} sono state incassate
            </p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div
      className={`
        rounded-xl border p-6
        transition-all duration-200
        ${darkMode
          ? "bg-neutral-900 border-neutral-800 shadow-md hover:shadow-lg"
          : "bg-white border-neutral-200 shadow-sm hover:shadow-md"
        }
      `}
    >
      {/* Header */}
      <div className="flex items-center justify-between mb-4">
        <h3 className={`text-sm font-medium ${darkMode ? "text-neutral-500" : "text-neutral-600"}`}>
          Fatture da incassare
        </h3>
        <button
          onClick={() => navigate(hasOverdue ? "/invoices?filter=overdue" : "/invoices")}
          className={`flex items-center gap-1 text-sm font-medium transition-colors ${
            darkMode
              ? "text-forfy-300 hover:text-forfy-300"
              : "text-forfy-600 hover:text-forfy-500"
          }`}
        >
          Vedi tutte
          <ArrowRight className="w-4 h-4" />
        </button>
      </div>

      {/* Summary */}
      <div className="flex items-baseline gap-3 mb-5">
        <span className={`text-2xl font-bold ${darkMode ? "text-white" : "text-neutral-900"}`}>
          {formatCurrency(totalAmount)}
        </span>
        <span className={`text-sm ${darkMode ? "text-neutral-500" : "text-neutral-600"}`}>
          {totalClients} client{totalClients === 1 ? "e" : "i"}
        </span>
      </div>

      {/* Client List */}
      <div className="space-y-3">
        {clientsData.slice(0, 4).map((client) => (
          <div
            key={client.name}
            className={`flex items-center gap-3 p-3 rounded-lg transition-colors ${
              darkMode ? "hover:bg-neutral-800/50" : "hover:bg-neutral-50"
            }`}
          >
            {/* Avatar */}
            <div
              className={`w-10 h-10 rounded-full flex items-center justify-center text-white text-sm font-semibold shrink-0 ${getClientColor(client.name).bg}`}
            >
              {getClientInitials(client.name)}
            </div>

            {/* Client Info */}
            <div className="flex-1 min-w-0">
              <div className="flex items-center gap-2">
                <span
                  className={`font-medium truncate ${
                    darkMode ? "text-white" : "text-neutral-900"
                  }`}
                >
                  {client.name}
                </span>
              </div>
              <div className="flex items-center gap-1.5 mt-1">
                {client.hasOverdue ? (
                  <>
                    <InvoiceStatusBadge status="overdue" darkMode={darkMode} size="sm" />
                    <span className={`text-xs ${darkMode ? "text-rose-400" : "text-rose-600"}`}>
                      dal {formatDate(client.oldestDueDate)}
                    </span>
                  </>
                ) : (
                  <>
                    <InvoiceStatusBadge status="pending" darkMode={darkMode} size="sm" />
                    <span className={`text-xs ${darkMode ? "text-neutral-600" : "text-neutral-500"}`}>
                      Scade tra {client.daysUntilDue} giorn{client.daysUntilDue === 1 ? "o" : "i"}
                    </span>
                  </>
                )}
              </div>
            </div>

            {/* Amount */}
            <div className="text-right shrink-0">
              <div className={`font-semibold ${darkMode ? "text-white" : "text-neutral-900"}`}>
                {formatCurrency(client.totalAmount)}
              </div>
              <div className={`text-xs ${darkMode ? "text-neutral-600" : "text-neutral-500"}`}>
                {client.invoices.length} fattur{client.invoices.length === 1 ? "a" : "e"}
              </div>
            </div>
          </div>
        ))}

        {/* Show more link */}
        {clientsData.length > 4 && (
          <button
            onClick={() => navigate("/invoices")}
            className={`w-full py-2 text-sm font-medium rounded-lg transition-colors ${
              darkMode
                ? "text-neutral-500 hover:text-neutral-400 hover:bg-neutral-800/50"
                : "text-neutral-600 hover:text-neutral-700 hover:bg-neutral-50"
            }`}
          >
            +{clientsData.length - 4} altri client{clientsData.length - 4 === 1 ? "e" : "i"}
          </button>
        )}
      </div>

      {/* Overdue Warning Badge (subtle, at bottom) */}
      {hasOverdue && (
        <div
          className={`mt-4 pt-4 border-t flex items-center gap-2 ${
            darkMode ? "border-neutral-800" : "border-neutral-100"
          }`}
        >
          <InvoiceStatusBadge status="overdue" darkMode={darkMode} size="sm" />
          <span className={`text-xs ${darkMode ? "text-rose-400" : "text-rose-600"}`}>
            {clientsData.filter((c) => c.hasOverdue).length} client
            {clientsData.filter((c) => c.hasOverdue).length === 1 ? "e" : "i"} con pagamenti scaduti
          </span>
        </div>
      )}
    </div>
  );
}
